// Spatial keys shared with scripts/build_data.py (both read grid.json).
import grid from './grid.json' with { type: 'json' }

const { origin, tile, cell, bbox } = grid

export const tileOf = (lat: number, lon: number) =>
  Math.floor((lat - origin.lat) / tile.dlat) * tile.stride + Math.floor((lon - origin.lon) / tile.dlon)

export const cellOf = (lat: number, lon: number) =>
  Math.floor((lat - origin.lat) / cell.dlat) * cell.stride + Math.floor((lon - origin.lon) / cell.dlon)

export const inBounds = (lat: number, lon: number) =>
  lat > bbox.lat0 && lat < bbox.lat1 && lon > bbox.lon0 && lon < bbox.lon1

const M_LAT = 111_320
const M_LON = 111_320 * Math.cos((34.05 * Math.PI) / 180)

/** Straight-line metres. Good to ~0.5% across LA; the city is small enough to treat as flat. */
export function meters(aLat: number, aLon: number, bLat: number, bLon: number): number {
  return Math.hypot((aLat - bLat) * M_LAT, (aLon - bLon) * M_LON)
}

/** Every tile within `radius` metres of a point. */
export function tilesAround(lat: number, lon: number, radius: number): number[] {
  const dLat = radius / M_LAT, dLon = radius / M_LON
  const out: number[] = []
  for (let a = Math.floor((lat - dLat - origin.lat) / tile.dlat); a <= Math.floor((lat + dLat - origin.lat) / tile.dlat); a++) {
    for (let b = Math.floor((lon - dLon - origin.lon) / tile.dlon); b <= Math.floor((lon + dLon - origin.lon) / tile.dlon); b++) {
      out.push(a * tile.stride + b)
    }
  }
  return out
}

export function decodePolyline(s: string, precision = 5): [number, number][] {
  const pts: [number, number][] = []
  const f = 10 ** precision
  let i = 0, lat = 0, lon = 0
  while (i < s.length) {
    for (let which = 0; which < 2; which++) {
      let shift = 0, res = 0, b: number
      do {
        b = s.charCodeAt(i++) - 63
        res |= (b & 0x1f) << shift
        shift += 5
      } while (b >= 0x20)
      const d = res & 1 ? ~(res >> 1) : res >> 1
      if (which === 0) lat += d
      else lon += d
    }
    pts.push([lat / f, lon / f])
  }
  return pts
}

/** Distance from a point to a polyline, metres. */
export function toLine(lat: number, lon: number, pts: [number, number][]): number {
  if (pts.length === 1) return meters(lat, lon, pts[0][0], pts[0][1])
  let best = Infinity
  for (let i = 0; i < pts.length - 1; i++) {
    const ax = (pts[i][1] - lon) * M_LON, ay = (pts[i][0] - lat) * M_LAT
    const bx = (pts[i + 1][1] - lon) * M_LON, by = (pts[i + 1][0] - lat) * M_LAT
    const dx = bx - ax, dy = by - ay
    const l2 = dx * dx + dy * dy
    const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / l2))
    best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy))
  }
  return best
}

// Street names to a matching key, the same way scripts/geocode.py does: "N Ogden Dr", "OGDEN DR"
// and "ogden drive" all become "OGDEN".
const TYPES = new Set(['AVENUE', 'AVE', 'AV', 'STREET', 'ST', 'BOULEVARD', 'BLVD', 'BL', 'DRIVE', 'DR', 'PLACE', 'PL', 'ROAD', 'RD', 'LANE', 'LN',
  'COURT', 'CT', 'WAY', 'WY', 'TERRACE', 'TER', 'TR', 'CIRCLE', 'CIR', 'PARKWAY', 'PKWY', 'HIGHWAY', 'HWY', 'SQUARE', 'SQ',
  'TRAIL', 'TRL', 'PLAZA', 'PLZ', 'PASEO', 'WALK', 'ALLEY', 'AL', 'LOOP', 'ROW', 'CRES', 'CRESCENT', 'PKY'])
const DIRS = new Set(['N', 'S', 'E', 'W', 'NORTH', 'SOUTH', 'EAST', 'WEST'])
const ORDINALS: Record<string, string> = { FIRST: '1ST', SECOND: '2ND', THIRD: '3RD', FOURTH: '4TH', FIFTH: '5TH', SIXTH: '6TH', SEVENTH: '7TH', EIGHTH: '8TH', NINTH: '9TH', TENTH: '10TH' }

export function streetKey(name: string): string {
  let w = name.toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/).filter(Boolean).map((x) => ORDINALS[x] ?? x)
  if (w.length > 1 && DIRS.has(w[0])) w = w.slice(1)
  while (w.length > 1 && (TYPES.has(w[w.length - 1]) || DIRS.has(w[w.length - 1]))) w = w.slice(0, -1)
  return w.join(' ')
}

/** Point in polygon by the even-odd rule over every ring (outer rings and holes alike). */
export function inRings(lat: number, lon: number, rings: [number, number][][]): boolean {
  let inside = false
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [ya, xa] = ring[i], [yb, xb] = ring[j]
      if ((ya > lat) !== (yb > lat) && lon < ((xb - xa) * (lat - ya)) / (yb - ya) + xa) inside = !inside
    }
  }
  return inside
}
